/**
 * The HTTP client.
 *
 * ## Token handling, and why the access token is a closure rather than a field
 *
 * The access token lives in a module-level variable owned by `AuthProvider`, and
 * is reached through a getter. It is deliberately *not* in `localStorage`:
 * `localStorage` is readable by any script on the origin, so a single
 * compromised dependency - a build tool, a compromised npm package in a
 * transitive dep - exfiltrates a working credential. In-memory means the
 * credential dies with the tab, and the thing that survives is the refresh token
 * in an `HttpOnly` cookie, which script cannot read at all.
 *
 * The cost is that a page reload loses the access token, which is why
 * `AuthProvider` calls `POST /api/auth/refresh` on mount. That is a round trip
 * the cookie-based alternative would not need, and it is a round trip worth
 * paying for.
 *
 * ## Refresh on 401, once
 *
 * `request` retries exactly once after a refresh. Retrying more than once is how
 * a client turns a permanently-expired session into an infinite loop against the
 * auth endpoint; retrying zero times means every user whose tab was open for
 * more than fifteen minutes sees a spurious error on their next click. The
 * retry flag is per-request, so two concurrent 401s produce two refreshes - the
 * alternative is a shared in-flight promise, which is more machinery than the
 * problem deserves given that refresh rotates the token and the second refresh
 * simply wins.
 *
 * ## Why the problem code is recovered from the `type` URI
 *
 * The Worker's problem body carries `type` (`https://slate.dev/problems/version-conflict`)
 * and deliberately does not duplicate the code as a separate member - see
 * `apps/api/src/errors.ts`. The client therefore un-slugifies the last path
 * segment. Doing it here, once, means no component ever parses a URI.
 */

import type { BoardEvent } from '@slate/shared';
import type {
  ActivityResponse,
  AttachmentListResponse,
  AttachmentWriteResponse,
  BoardListResponse,
  BoardResponse,
  BoardStateResponse,
  CardDetailResponse,
  CardPatchBody,
  CardWriteResponse,
  ColumnWriteResponse,
  CommentWriteResponse,
  MeResponse,
  SessionResponse,
  WorkspaceDetailResponse,
  WorkspaceListResponse,
} from './types.js';

/**
 * A rejected request, carrying the server's problem document.
 *
 * `code` is the machine-readable discriminant and is what callers switch on.
 * `detail` is prose for the user. `current` is present on `version_conflict` and
 * is the whole rollback mechanism - see `state/board-reducer.ts`.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: string;
  readonly requestId: string | null;
  readonly errors: { path: string; message: string }[];
  readonly current: unknown;
  /**
   * Every extension member the problem document carried, minus the ones promoted
   * to fields above.
   *
   * RFC 9457 explicitly permits extension members, and the two the API uses are
   * `current` (on a version conflict) and `cardCount` (on a refused column
   * deletion). Capturing the whole object means a new extension can be consumed by
   * a caller without a change here - and the alternative, one field per
   * extension, is a client that has to be redeployed every time the server
   * attaches one more fact to an error.
   */
  readonly extensions: Record<string, unknown>;

  constructor(args: {
    status: number;
    code: string;
    detail: string;
    requestId: string | null;
    errors: { path: string; message: string }[];
    current: unknown;
    extensions: Record<string, unknown>;
  }) {
    super(args.detail);
    this.name = 'ApiError';
    this.status = args.status;
    this.code = args.code;
    this.detail = args.detail;
    this.requestId = args.requestId;
    this.errors = args.errors;
    this.current = args.current;
    this.extensions = args.extensions;
  }

  /** True for a conflict the caller can resolve by re-reading and retrying. */
  get isConflict(): boolean {
    return this.code === 'version_conflict' || this.code === 'conflict';
  }

  /** True when the session is gone and the user must sign in again. */
  get isAuthFailure(): boolean {
    return this.status === 401;
  }
}

interface ProblemBody {
  type?: unknown;
  status?: unknown;
  detail?: unknown;
  requestId?: unknown;
  errors?: unknown;
  current?: unknown;
}

const PROBLEM_PREFIX = 'https://slate.dev/problems/';

function codeFromType(type: unknown): string {
  if (typeof type !== 'string') return 'unknown';
  const slug = type.startsWith(PROBLEM_PREFIX) ? type.slice(PROBLEM_PREFIX.length) : type.split('/').pop() ?? type;
  return slug.replace(/-/g, '_');
}

function parseFieldErrors(value: unknown): { path: string; message: string }[] {
  if (!Array.isArray(value)) return [];
  const out: { path: string; message: string }[] = [];
  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) continue;
    const record = entry as { path?: unknown; message?: unknown };
    out.push({
      path: typeof record.path === 'string' ? record.path : 'request',
      message: typeof record.message === 'string' ? record.message : 'is invalid',
    });
  }
  return out;
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: ProblemBody = {};
  try {
    const parsed: unknown = await response.json();
    if (typeof parsed === 'object' && parsed !== null) body = parsed as ProblemBody;
  } catch {
    // A non-JSON error body - a proxy's HTML error page, or a truncated
    // response. Falling through to the status-derived defaults is better than
    // throwing a parse error on top of an error.
  }

  const detail =
    typeof body.detail === 'string' && body.detail.length > 0
      ? body.detail
      : `The request failed with status ${response.status}.`;

  // The extension members are everything the problem body carried that is not one
  // of the five RFC 9457 members. Copying rather than aliasing so that a caller
  // mutating the record cannot reach back into the parsed response.
  const extensions: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(body)) {
    if (key === 'type' || key === 'title' || key === 'status' || key === 'detail' || key === 'instance' || key === 'requestId') {
      continue;
    }
    extensions[key] = value;
  }

  return new ApiError({
    status: response.status,
    code: codeFromType(body.type),
    detail,
    requestId: typeof body.requestId === 'string' ? body.requestId : null,
    errors: parseFieldErrors(body.errors),
    current: body.current ?? null,
    extensions,
  });
}

export interface ApiClientHooks {
  /** The current access token, or `null` when signed out. */
  getAccessToken(): string | null;
  /** Attempt a refresh. Resolves to the new token, or `null` if the session is
   * gone. Must not throw. */
  refresh(): Promise<string | null>;
  /** Called when the session is definitively gone, so the app can route to the
   * sign-in screen exactly once rather than on every failed request. */
  onSessionLost(): void;
}

/**
 * Exchange the refresh cookie for a new session.
 *
 * A free function rather than only a method, because it must be callable from
 * places that do not have a client yet - `AuthProvider`'s initial probe, and the
 * client's own 401 retry. It deliberately does not go through `ApiClient.json`,
 * which would route a 401 on the refresh endpoint back into the refresh path and
 * loop.
 */
export async function refreshSessionRequest(): Promise<SessionResponse | null> {
  const response = await fetch('/api/auth/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
    credentials: 'same-origin',
  });
  if (!response.ok) return null;
  return (await response.json()) as SessionResponse;
}

export class ApiClient {
  constructor(private readonly hooks: ApiClientHooks) {}

  // -------------------------------------------------------------------------
  // Core
  // -------------------------------------------------------------------------

  private async send(path: string, init: RequestInit, allowRetry: boolean): Promise<Response> {
    const token = this.hooks.getAccessToken();
    const headers = new Headers(init.headers);
    if (token !== null) headers.set('Authorization', `Bearer ${token}`);

    const response = await fetch(path, {
      ...init,
      headers,
      // Same-origin credentials. The refresh cookie is scoped to `/api/auth`
      // and is the only cookie this app sets; sending it everywhere costs
      // nothing because the browser will not attach it outside its path.
      credentials: 'same-origin',
    });

    if (response.status !== 401 || !allowRetry) return response;

    const refreshed = await this.hooks.refresh();
    if (refreshed === null) {
      this.hooks.onSessionLost();
      return response;
    }

    // The retry is unconditional and idempotent by construction: a 401 means the
    // request was rejected before it was processed, so re-sending it cannot
    // double-apply a mutation.
    return this.send(path, init, false);
  }

  private async json<T>(path: string, init: RequestInit = {}, allowRetry = true): Promise<T> {
    const headers = new Headers(init.headers);
    if (init.body !== undefined) headers.set('Content-Type', 'application/json');

    const response = await this.send(path, { ...init, headers }, allowRetry);
    if (!response.ok) throw await toApiError(response);

    // A 204 has no body. Returning `undefined as T` is a lie the call sites opt
    // into by asking for `void`, which is the honest way to express it.
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  // -------------------------------------------------------------------------
  // Auth
  // -------------------------------------------------------------------------

  register(body: { email: string; name: string; password: string }): Promise<SessionResponse> {
    return this.json<SessionResponse>('/api/auth/register', { method: 'POST', body: JSON.stringify(body) });
  }

  login(body: { email: string; password: string }): Promise<SessionResponse> {
    return this.json<SessionResponse>('/api/auth/login', { method: 'POST', body: JSON.stringify(body) });
  }

  /** Called by `AuthProvider` directly rather than through `request`, because
   * the refresh flow must not itself trigger a refresh. */
  refreshSession(): Promise<SessionResponse | null> {
    return refreshSessionRequest();
  }

  async logout(): Promise<void> {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
  }

  me(): Promise<MeResponse> {
    return this.json<MeResponse>('/api/auth/me');
  }

  // -------------------------------------------------------------------------
  // Workspaces
  // -------------------------------------------------------------------------

  listWorkspaces(): Promise<WorkspaceListResponse> {
    return this.json<WorkspaceListResponse>('/api/workspaces');
  }

  getWorkspace(slug: string): Promise<WorkspaceDetailResponse> {
    return this.json<WorkspaceDetailResponse>(`/api/workspaces/${encodeURIComponent(slug)}`);
  }

  createWorkspace(body: { name: string; slug?: string }): Promise<{ workspace: WorkspaceDetailResponse['workspace'] }> {
    return this.json('/api/workspaces', { method: 'POST', body: JSON.stringify(body) });
  }

  // -------------------------------------------------------------------------
  // Boards
  // -------------------------------------------------------------------------

  listBoards(workspaceSlug: string): Promise<BoardListResponse> {
    return this.json<BoardListResponse>(`/api/boards?workspace=${encodeURIComponent(workspaceSlug)}`);
  }

  getBoard(boardId: string): Promise<BoardResponse> {
    return this.json<BoardResponse>(`/api/boards/${boardId}`);
  }

  /**
   * Create a board.
   *
   * The workspace travels as a slug in `?workspace=`, the same parameter
   * `listBoards` uses. Creating a board is a workspace-scoped action: the
   * permission being checked is `board:create` *on the workspace*, and a board
   * id does not exist yet to check it against - so the parent is named in the
   * query rather than in the path. See the route handler.
   */
  createBoard(workspaceSlug: string, body: { name: string; description?: string; columns?: string[] }): Promise<BoardResponse> {
    return this.json<BoardResponse>(`/api/boards?workspace=${encodeURIComponent(workspaceSlug)}`, {
      method: 'POST',
      body: JSON.stringify({ name: body.name, description: body.description, columns: body.columns }),
    });
  }

  /**
   * Board state, with or without a delta cursor.
   *
   * `since` is the highest sequence the client has applied. Passing it is what
   * turns a reconnect into "apply the four events you missed" rather than "throw
   * the board away and refetch it" - and the server decides whether the gap is
   * small enough to replay or whether a full snapshot is cheaper.
   */
  boardState(boardId: string, since?: number): Promise<BoardStateResponse> {
    const suffix = since === undefined ? '' : `?since=${since}`;
    return this.json<BoardStateResponse>(`/api/boards/${boardId}/state${suffix}`);
  }

  activity(boardId: string, cursor?: string, limit = 30): Promise<ActivityResponse> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor !== undefined) params.set('cursor', cursor);
    return this.json<ActivityResponse>(`/api/boards/${boardId}/activity?${params.toString()}`);
  }

  archiveBoard(boardId: string, archived: boolean): Promise<{ ok: boolean; archived: boolean }> {
    return this.json(`/api/boards/${boardId}/archive`, { method: 'POST', body: JSON.stringify({ archived }) });
  }

  /** Irreversible. Requires `board:delete`, which only owners and admins hold. */
  deleteBoard(boardId: string): Promise<void> {
    return this.json<void>(`/api/boards/${boardId}`, { method: 'DELETE' });
  }

  // -------------------------------------------------------------------------
  // Cards - the HTTP fallback for mutations
  // -------------------------------------------------------------------------

  /**
   * These are the HTTP half of the dual-transport mutation path.
   *
   * The WebSocket is the primary transport, but a socket spends part of its life
   * reconnecting, and a board that refuses every edit for the two seconds a
   * reconnect takes is a board that feels broken. Every mutation therefore has an
   * HTTP form, and `BoardProvider` picks whichever is available. Both go through
   * the same Durable Object (the REST routes are thin proxies over
   * `POST /mutate`), so the validation, the version check and the event are
   * identical whichever path was taken - which is the property that makes the
   * fallback safe rather than a second implementation.
   */
  createCard(boardId: string, body: Record<string, unknown>): Promise<CardWriteResponse> {
    return this.json<CardWriteResponse>(`/api/boards/${boardId}/cards`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  cardDetail(boardId: string, cardId: string): Promise<CardDetailResponse> {
    return this.json<CardDetailResponse>(`/api/boards/${boardId}/cards/${cardId}`);
  }

  updateCard(boardId: string, cardId: string, patch: CardPatchBody, expectedVersion: number): Promise<CardWriteResponse> {
    return this.json<CardWriteResponse>(`/api/boards/${boardId}/cards/${cardId}`, {
      method: 'PATCH',
      // `If-Match` is the primary mechanism; the body field is what the socket
      // path uses and keeping both means one code path on the server.
      headers: { 'If-Match': String(expectedVersion) },
      body: JSON.stringify({ ...patch, expectedVersion }),
    });
  }

  moveCard(
    boardId: string,
    cardId: string,
    body: { columnId: string; position: number; expectedVersion: number; opId: string },
  ): Promise<CardWriteResponse> {
    return this.json<CardWriteResponse>(`/api/boards/${boardId}/cards/${cardId}/move`, {
      method: 'POST',
      headers: { 'If-Match': String(body.expectedVersion) },
      body: JSON.stringify(body),
    });
  }

  archiveCard(boardId: string, cardId: string, archived: boolean, expectedVersion: number): Promise<CardWriteResponse> {
    return this.json<CardWriteResponse>(`/api/boards/${boardId}/cards/${cardId}/archive`, {
      method: 'POST',
      headers: { 'If-Match': String(expectedVersion) },
      body: JSON.stringify({ archived, expectedVersion }),
    });
  }

  deleteCard(boardId: string, cardId: string, expectedVersion: number): Promise<void> {
    return this.json<void>(`/api/boards/${boardId}/cards/${cardId}?expectedVersion=${expectedVersion}`, {
      method: 'DELETE',
    });
  }

  // -------------------------------------------------------------------------
  // Columns
  // -------------------------------------------------------------------------

  createColumn(boardId: string, name: string): Promise<ColumnWriteResponse> {
    return this.json<ColumnWriteResponse>(`/api/boards/${boardId}/columns`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  updateColumn(
    boardId: string,
    columnId: string,
    patch: { name?: string; position?: number },
  ): Promise<ColumnWriteResponse> {
    return this.json<ColumnWriteResponse>(`/api/boards/${boardId}/columns/${columnId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  deleteColumn(
    boardId: string,
    columnId: string,
    strategy?: { kind: 'archive' } | { kind: 'move'; targetColumnId: string },
  ): Promise<{ ok: boolean; sequence: number | null }> {
    const params = new URLSearchParams();
    if (strategy?.kind === 'archive') params.set('strategy', 'archive');
    if (strategy?.kind === 'move') {
      params.set('strategy', 'move');
      params.set('targetColumnId', strategy.targetColumnId);
    }
    const suffix = params.toString() === '' ? '' : `?${params.toString()}`;
    return this.json(`/api/boards/${boardId}/columns/${columnId}${suffix}`, { method: 'DELETE' });
  }

  // -------------------------------------------------------------------------
  // Comments and attachments
  // -------------------------------------------------------------------------

  createComment(boardId: string, cardId: string, body: string): Promise<CommentWriteResponse> {
    return this.json<CommentWriteResponse>(`/api/boards/${boardId}/cards/${cardId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    });
  }

  updateComment(boardId: string, cardId: string, commentId: string, body: string): Promise<CommentWriteResponse> {
    return this.json<CommentWriteResponse>(`/api/boards/${boardId}/cards/${cardId}/comments/${commentId}`, {
      method: 'PATCH',
      body: JSON.stringify({ body }),
    });
  }

  deleteComment(boardId: string, cardId: string, commentId: string): Promise<void> {
    return this.json<void>(`/api/boards/${boardId}/cards/${cardId}/comments/${commentId}`, { method: 'DELETE' });
  }

  listAttachments(boardId: string, cardId: string): Promise<AttachmentListResponse> {
    return this.json<AttachmentListResponse>(`/api/boards/${boardId}/cards/${cardId}/attachments`);
  }

  /**
   * Upload.
   *
   * `FormData` and not a JSON body, so no `Content-Type` header is set - the
   * browser must be allowed to generate the multipart boundary itself. Setting
   * `Content-Type: multipart/form-data` by hand omits the boundary and the
   * server's `formData()` parse fails with a 415 that looks like a server bug.
   */
  async uploadAttachment(boardId: string, cardId: string, file: File): Promise<AttachmentWriteResponse> {
    const form = new FormData();
    form.append('file', file);
    return this.json<AttachmentWriteResponse>(`/api/boards/${boardId}/cards/${cardId}/attachments`, {
      method: 'POST',
      body: form,
    });
  }

  deleteAttachment(boardId: string, cardId: string, attachmentId: string): Promise<void> {
    return this.json<void>(`/api/boards/${boardId}/cards/${cardId}/attachments/${attachmentId}`, { method: 'DELETE' });
  }

  // -------------------------------------------------------------------------
  // Health
  // -------------------------------------------------------------------------

  /** `ready` counts users and pings a Durable Object, so it is the endpoint that
   * answers "is the whole stack up" rather than "is the Worker answering". */
  ready(): Promise<{ status: string; checks?: Record<string, unknown> }> {
    return this.json('/api/ready');
  }
}

/** The capability URL for an attachment's bytes.
 *
 * The key is unguessable and the route is public - see `apps/api/src/routes/media.ts`.
 * Building the URL here rather than storing it on the attachment keeps the API
 * surface stable across a change of storage driver: the client never learns
 * whether the bytes are in R2 or in a D1 BLOB. */
export function mediaUrl(assetKey: string): string {
  return `/api/media/${assetKey}`;
}

/** Re-exported so a component can type a callback without importing
 * `@slate/shared` alongside this module. */
export type { BoardEvent };
